import { useEffect, useRef, useState } from "react";
import { 
  Phone, MessageCircle, ArrowRight, Shield, Sparkles, 
  Users, MapPin, Heart, Navigation
} from "lucide-react";
import { clinic, branches } from "../data";
import { quotes } from "../data/quotes";

export default function Hero({ isPageLoaded = false, selectedBranch }) {
  const heroRef = useRef(null);
  const cardRef = useRef(null);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const currentBranch = branches[selectedBranch];
  const branchList = Object.values(branches);

  useEffect(() => {
    if (isPageLoaded) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isPageLoaded]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateXValue = ((y - centerY) / centerY) * 10;
    const rotateYValue = ((x - centerX) / centerX) * 10;
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleTouchMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateXValue = ((y - centerY) / centerY) * 10;
    const rotateYValue = ((x - centerX) / centerX) * 10;
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleReset = () => {
    setRotateX(0);
    setRotateY(0);
  };

  const handleBookAppointment = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleGetDirections = () => {
    if (currentBranch?.mapLink) {
      window.open(currentBranch.mapLink, "_blank");
    }
  };

  const getBranchColor = (branchId) => {
    const colors = {
      mangalam: "bg-orange-100 text-orange-700",
      koottayi: "bg-green-100 text-green-700",
      vettom: "bg-teal-100 text-teal-700",
    };
    return colors[branchId] || "bg-gray-100 text-gray-700";
  };

  // Render quote icon - using emojis directly
  const renderQuoteIcon = (icon) => {
    return <span className="text-2xl md:text-3xl">{icon}</span>;
  };

  return (
    <section 
      ref={heroRef} 
      className={`relative min-h-screen flex items-center overflow-hidden pt-16 md:pt-20 transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f0f5ed] to-[#e8ece4]"></div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-0">
          <svg className="relative block w-full h-[40px] md:h-[100px]" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="#4a7c59" fillOpacity="0.08"></path>
          </svg>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className={`mb-4 md:mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-xl md:text-3xl font-bold text-[#4a7c59] tracking-wide">{clinic.name}</h2>
              <p className="text-sm md:text-base text-[#2d5a3f] font-semibold tracking-wide">{clinic.tagline}</p>
              <div className="w-12 h-0.5 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mt-2 mx-auto lg:mx-0"></div>
            </div>

            {/* FIXED: Removed "Trusted" - using "Dental Excellence" instead */}
            <div className={`inline-flex items-center px-3 py-1 md:px-4 md:py-2 bg-[#4a7c59]/10 rounded-full mb-4 md:mb-6 mx-auto lg:mx-0 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
              <Shield className="w-3 h-3 md:w-4 md:h-4 text-[#4a7c59] mr-1 md:mr-2" />
              <span className="text-xs md:text-sm text-[#4a7c59] font-semibold">{clinic.experience} Years of Dental Excellence</span>
            </div>
            
            <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6">
                <div className="text-gray-800">
                  Trusted Dental Clinic in <span className="text-[#4a7c59]">Mangalam, Koottayi & Vettom</span>
                </div>
                <div className="relative inline-block mt-1 md:mt-2">
                  <span className="relative z-10">
                    <span className="text-gradient-olive">Healthy Smiles</span>
                    <span className="text-gray-800"> Begin with Expert Dental Care</span>
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -translate-x-full animate-shine rounded-lg"></span>
                </div>
              </h1>
            </div>
            
            {/* FIXED: Split into two paragraphs for better UX */}
            <div className={`mb-4 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <p className="text-gray-600 text-sm md:text-lg leading-relaxed">
                Olive Dental Clinic is one of the trusted choices for patients looking for the best dental clinic in <span className="font-semibold text-[#4a7c59]">Mangalam</span>, <span className="font-semibold text-[#4a7c59]">Koottayi</span> and <span className="font-semibold text-[#4a7c59]">Vettom</span>. Our experienced dentists provide comprehensive dental care using modern technology for patients of all ages.
              </p>
            </div>
            <div className={`mb-6 md:mb-8 transition-all duration-700 delay-450 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <p className="text-gray-600 text-sm md:text-lg leading-relaxed">
                We offer <span className="font-medium text-[#4a7c59]">Dental Implants</span>, <span className="font-medium text-[#4a7c59]">Root Canal Treatment</span>, <span className="font-medium text-[#4a7c59]">Teeth Cleaning</span>, <span className="font-medium text-[#4a7c59]">Aligners</span>, <span className="font-medium text-[#4a7c59]">Cosmetic Dentistry</span>, <span className="font-medium text-[#4a7c59]">Kids Dentistry</span>, <span className="font-medium text-[#4a7c59]">Tooth Extraction</span>, <span className="font-medium text-[#4a7c59]">Teeth Whitening</span> and <span className="font-medium text-[#4a7c59]">Smile Makeover</span> at our branches.
              </p>
              <div className="mt-3 flex items-center gap-2 justify-center lg:justify-start">
                <div className="h-px w-8 md:w-12 bg-gradient-to-r from-[#4a7c59] to-transparent"></div>
                <span className="text-xs md:text-sm text-[#4a7c59] font-medium">{clinic.name} Promise</span>
              </div>
            </div>

            <div className={`mb-6 md:mb-8 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
              <div className="relative group">
                <div className="bg-gradient-to-br from-white to-[#4a7c59]/5 rounded-xl md:rounded-2xl p-3 md:p-5 shadow-lg border border-[#4a7c59]/20">
                  <div className="flex items-center gap-3 md:gap-4 justify-center lg:justify-start">
                    <div className="relative w-10 h-10 md:w-14 md:h-14 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-xl flex items-center justify-center shadow-lg">
                      <Users className="w-5 h-5 md:w-7 md:h-7 text-white" />
                    </div>
                    <div className="flex-1 text-center lg:text-left">
                      <div className="flex items-baseline gap-2 flex-wrap justify-center lg:justify-start">
                        <span className="text-2xl md:text-5xl font-bold text-gradient-olive">{clinic.happyPatients}</span>
                        <Sparkles className="w-4 h-4 md:w-5 h-5 text-yellow-500" />
                        <span className="text-xs md:text-sm text-[#4a7c59] font-medium ml-1">Happy Patients</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 justify-center lg:justify-start">
                        <div className="h-0.5 w-6 md:w-8 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-full"></div>
                        <p className="text-gray-500 text-xs">Across 3 Branches</p>
                        <div className="h-0.5 w-6 md:w-8 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  {/* FIXED: Removed progress bar for cleaner look */}
                  <div className="flex justify-between items-center mt-3 text-xs text-gray-400 flex-wrap gap-2 justify-center">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-[#4a7c59]" />
                      {clinic.branches} Branches
                    </span>
                    {/* FIXED: Changed to "Patient-Centered Care" instead of repeating "Happy Patients" */}
                    <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3 text-red-500 fill-red-500" />
                      Patient-Centered Care
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-[#4a7c59]">🦷</span>
                      Modern Technology
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={`mb-6 md:mb-8 p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-md transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-start gap-2 md:gap-3 justify-center lg:justify-start">
                <div className="text-2xl md:text-3xl">
                  {renderQuoteIcon(quotes[currentQuote].icon)}
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <p className="text-xs md:text-sm text-gray-700 font-medium italic">"{quotes[currentQuote].text}"</p>
                  <p className="text-xs text-[#4a7c59] mt-1 font-semibold">— {quotes[currentQuote].author}</p>
                  <div className="flex gap-1 mt-2 justify-center lg:justify-start">
                    {quotes.map((_, idx) => (
                      <div key={idx} className={`h-1 rounded-full transition-all duration-300 ${idx === currentQuote ? 'w-4 md:w-6 bg-[#4a7c59]' : 'w-1.5 md:w-2 bg-gray-300'}`}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start transition-all duration-700 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <button onClick={handleBookAppointment} className="group inline-flex items-center justify-center px-6 md:px-8 py-2.5 md:py-3 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 relative overflow-hidden text-sm md:text-base">
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                <MessageCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 group-hover:animate-bounce" />
                Book Appointment
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href={`tel:${currentBranch.contact.phone}`} className="inline-flex items-center justify-center px-6 md:px-8 py-2.5 md:py-3 border-2 border-[#4a7c59] text-[#4a7c59] rounded-full font-semibold hover:bg-[#4a7c59] hover:text-white transition-all duration-300 group text-sm md:text-base">
                <Phone className="w-4 h-4 md:w-5 md:h-5 mr-2 group-hover:animate-pulse" />
                Call Now
              </a>
              <button onClick={handleGetDirections} className="inline-flex items-center justify-center px-6 md:px-8 py-2.5 md:py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-100 hover:border-[#4a7c59] transition-all duration-300 group text-sm md:text-base">
                <Navigation className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Get Directions
              </button>
            </div>

            <div className={`mt-4 text-center lg:text-left transition-all duration-700 delay-800 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <p className="text-[10px] md:text-xs text-gray-400">
                Serving patients across <span className="text-gray-500 font-medium">Tirur</span>, <span className="text-gray-500 font-medium">Mangalam</span>, <span className="text-gray-500 font-medium">Koottayi</span>, <span className="text-gray-500 font-medium">Vettom</span> and <span className="text-gray-500 font-medium">Malappuram District</span>.
              </p>
            </div>
          </div>

          <div className={`mt-8 lg:mt-0 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}>
            <div className="relative flex justify-center perspective-1000">
              <div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleReset}
                onMouseLeave={handleReset}
                className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md mx-auto transition-transform duration-300 ease-out cursor-pointer"
                style={{
                  transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
                  transformStyle: 'preserve-3d',
                }}
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4a7c59] via-[#8fbc8f] to-[#2d5a3f] rounded-xl md:rounded-2xl blur-md opacity-75 animate-border-light"></div>
                <div className="absolute -inset-2 md:-inset-4 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-xl md:rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-all duration-500"></div>
                <div className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl overflow-hidden border border-white/20">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 rounded-xl md:rounded-2xl border-2 border-transparent bg-gradient-to-r from-[#4a7c59]/20 via-transparent to-[#2d5a3f]/20 animate-border-shine"></div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                  <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/40 to-transparent rotate-45 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 pointer-events-none"></div>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f]"></div>
                  
                  <div className="p-4 md:p-8">
                    <img 
                      src="/images/olive2.png" 
                      alt={clinic.name}
                      className="w-full h-auto max-w-[180px] sm:max-w-[220px] md:max-w-full mx-auto object-contain"
                    />
                  </div>
                  
                  <div className="relative pb-4 md:pb-6">
                    <div className="flex items-center justify-center gap-1 md:gap-2">
                      <div className="h-px w-4 md:w-8 bg-gradient-to-r from-transparent to-[#4a7c59]"></div>
                      <div className="flex items-center gap-1 md:gap-2">
                        <Heart className="w-3 h-3 md:w-4 md:h-4 text-red-500 fill-red-500 animate-pulse" />
                        <span className="text-[10px] md:text-xs text-gray-500 font-medium">{clinic.experience} of Excellence</span>
                        <Heart className="w-3 h-3 md:w-4 md:h-4 text-red-500 fill-red-500 animate-pulse" />
                      </div>
                      <div className="h-px w-4 md:w-8 bg-gradient-to-l from-transparent to-[#4a7c59]"></div>
                    </div>
                    
                    <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 mt-2 md:mt-3">
                      {branchList.map((branch) => (
                        <div 
                          key={branch.id} 
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 md:px-2 md:py-1 rounded-full ${
                            selectedBranch === branch.id 
                              ? 'bg-[#4a7c59] text-white' 
                              : getBranchColor(branch.id)
                          }`}
                        >
                          <MapPin className="w-2.5 h-2.5" />
                          <span className="text-[10px] md:text-xs">{branch.shortName}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-center items-center gap-2 mt-2 md:mt-3">
                      <div className="flex items-center gap-1 px-3 py-1 bg-[#4a7c59]/10 rounded-full">
                        <Shield className="w-3 h-3 text-[#4a7c59]" />
                        <span className="text-[10px] md:text-xs text-gray-600 font-medium">Trusted Since {clinic.established}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gentle-wave {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-5px); }
        }
        @media (min-width: 768px) {
          @keyframes gentle-wave {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(-10px); }
          }
        }
        .animate-gentle-wave {
          animation: gentle-wave 8s ease-in-out infinite;
        }
        @keyframes borderLight {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.02); }
        }
        @keyframes borderShine {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-border-light {
          animation: borderLight 2s ease-in-out infinite;
        }
        .animate-border-shine {
          background: linear-gradient(90deg, transparent, rgba(74, 124, 89, 0.3), transparent);
          background-size: 200% 100%;
          animation: borderShine 3s linear infinite;
        }
      `}</style>
    </section>
  );
}